import React, { useState, useEffect, Fragment } from "react";
import { Modal, Table, Spinner, Badge } from "react-bootstrap";
import axiosInstance from "../../services/AxiosInstance";
import swal from "sweetalert";

const defaultFeatures = {
  apiAccess:      false,
  webhookAccess:  false,
  buttons:        true,
  ctaButtons:     true,
  listMenu:       true,
  polls:          true,
  autoReply:      false,
  welcomeMessage: false,
  templates:      true,
};

const defaultLimits = {
  messagesPerMonth:    1000,
  contactsPerCampaign: 500,
  maxInstances:        1,
};

const defaultCS = {
  enabled:                    false,
  maxChannels:                0,
  maxPostsPerChannelPerDay:   0,
  maxPostsPerChannelPerMonth: 0,
  maxScheduleDaysAhead:       7,
  allowImage:                 false,
  allowVideo:                 false,
  allowDocument:              false,
  allowPoll:                  false,
  allowBulkExcel:             false,
  allowRecurring:             false,
  allowTemplates:             false,
  maxTemplates:               0,
  allowAICheck:               false,
  allowAutopilot:             false,
  maxSources:                 0,
  maxAIPostsPerMonth:         0,
};

const emptyPack = {
  name:             "",
  description:      "",
  billingCycle:     "monthly",
  regularPrice:     "",
  salePrice:        "",
  days:             30,
  isActive:         true,
  sortOrder:        0,
  hasTrial:         false,
  trialDays:        7,
  features:         { ...defaultFeatures },
  limits:           { ...defaultLimits },
  channelScheduler: { ...defaultCS },
};

const Plans = () => {
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState({ ...emptyPack, features: { ...defaultFeatures }, limits: { ...defaultLimits }, channelScheduler: { ...defaultCS } });
  const [showCS, setShowCS]         = useState(false);
  const [saving, setSaving]         = useState(false);

  useEffect(() => { loadPlans(); }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("packs");
      if (data.status) setPlans(data.packs);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyPack, features: { ...defaultFeatures }, limits: { ...defaultLimits }, channelScheduler: { ...defaultCS } });
    setShowCS(false);
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      name:         plan.name         || "",
      description:  plan.description  || "",
      billingCycle: plan.billingCycle  || "monthly",
      regularPrice: plan.regularPrice  ?? "",
      salePrice:    plan.salePrice     ?? "",
      days:         plan.days          ?? 30,
      isActive:     plan.isActive      ?? true,
      sortOrder:    plan.sortOrder     ?? 0,
      hasTrial:     plan.hasTrial      ?? false,
      trialDays:    plan.trialDays     ?? 7,
      features:         { ...defaultFeatures, ...(plan.features         || {}) },
      limits:           { ...defaultLimits,   ...(plan.limits           || {}) },
      channelScheduler: { ...defaultCS,       ...(plan.channelScheduler || {}) },
    });
    setShowCS(!!(plan.channelScheduler?.enabled));
    setShowModal(true);
  };

  const onDelete = async (plan) => {
    const confirm = await swal({
      title: "Delete Plan?",
      text: `"${plan.name}" will be permanently deleted.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      const { data } = await axiosInstance.delete(`packs/${plan.id}`);
      if (data.status) {
        swal("Deleted!", "Plan has been deleted.", "success");
        loadPlans();
      } else {
        swal("Error", data.message, "error");
      }
    } catch (e) { swal("Error", e.message, "error"); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (editId) {
        res = await axiosInstance.put(`packs/${editId}`, form);
      } else {
        res = await axiosInstance.post("packs", form);
      }
      const { data } = res;
      if (data.status) {
        swal("Success", editId ? "Plan updated!" : "Plan created!", "success");
        setShowModal(false);
        loadPlans();
      } else {
        swal("Error", data.message, "error");
      }
    } catch (e) { swal("Error", e.message, "error"); }
    setSaving(false);
  };

  const setField    = (key, val)             => setForm(f => ({ ...f, [key]: val }));
  const setFeature  = (key, val)             => setForm(f => ({ ...f, features: { ...f.features, [key]: val } }));
  const setLimit    = (key, val)             => setForm(f => ({ ...f, limits:   { ...f.limits,   [key]: Number(val) } }));
  const setCS       = (key, val)             => setForm(f => ({ ...f, channelScheduler: { ...f.channelScheduler, [key]: val } }));
  const setCSNum    = (key, val)             => setCS(key, Number(val));

  const FeatureRow = ({ label, fkey }) => (
    <div className="form-group col-md-4">
      <label className="text-black font-w500">{label}</label>
      <select className="form-control" value={form.features[fkey] ? "Yes" : "No"}
        onChange={e => setFeature(fkey, e.target.value === "Yes")}>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );

  return (
    <>
      {/* ── Create / Edit Modal ─────────────────────────────────────────── */}
      <Modal className="modal fade" show={showModal} onHide={() => setShowModal(false)} size="lg">
        <div role="document">
          <form onSubmit={onSubmit}>
            <div className="modal-header">
              <h4 className="modal-title fs-20">{editId ? "Edit Plan" : "Create Plan"}</h4>
              <button type="button" className="close" onClick={() => setShowModal(false)}>
                <span>×</span>
              </button>
            </div>

            <div className="modal-body">
              {/* ── Basic Info ────────────────────────────────────────────── */}
              <h6 className="text-primary mb-3">Basic Info</h6>
              <div className="row">
                <div className="form-group col-md-6">
                  <label className="text-black font-w500">Plan Name *</label>
                  <input type="text" className="form-control" required value={form.name}
                    onChange={e => setField("name", e.target.value)} placeholder="e.g. Starter" />
                </div>
                <div className="form-group col-md-6">
                  <label className="text-black font-w500">Billing Cycle</label>
                  <select className="form-control" value={form.billingCycle}
                    onChange={e => setField("billingCycle", e.target.value)}>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-12">
                  <label className="text-black font-w500">Description *</label>
                  <textarea className="form-control" required rows={2} value={form.description}
                    onChange={e => setField("description", e.target.value)} placeholder="Short description" />
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-3">
                  <label className="text-black font-w500">Regular Price (₹) *</label>
                  <input type="number" className="form-control" required value={form.regularPrice}
                    onChange={e => setField("regularPrice", e.target.value)} placeholder="e.g. 999" />
                </div>
                <div className="form-group col-md-3">
                  <label className="text-black font-w500">Sale Price (₹) *</label>
                  <input type="number" className="form-control" required value={form.salePrice}
                    onChange={e => setField("salePrice", e.target.value)} placeholder="e.g. 799" />
                </div>
                <div className="form-group col-md-3">
                  <label className="text-black font-w500">Validity (days) *</label>
                  <input type="number" className="form-control" required value={form.days}
                    onChange={e => setField("days", Number(e.target.value))} placeholder="30" />
                </div>
                <div className="form-group col-md-3">
                  <label className="text-black font-w500">Sort Order</label>
                  <input type="number" className="form-control" value={form.sortOrder}
                    onChange={e => setField("sortOrder", Number(e.target.value))} />
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-4">
                  <label className="text-black font-w500">Status</label>
                  <select className="form-control" value={form.isActive ? "Active" : "Inactive"}
                    onChange={e => setField("isActive", e.target.value === "Active")}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group col-md-4">
                  <label className="text-black font-w500">Free Trial</label>
                  <select className="form-control" value={form.hasTrial ? "Yes" : "No"}
                    onChange={e => setField("hasTrial", e.target.value === "Yes")}>
                    <option value="No">No Trial</option>
                    <option value="Yes">Yes — Enable Trial</option>
                  </select>
                </div>
                {form.hasTrial && (
                  <div className="form-group col-md-4">
                    <label className="text-black font-w500">Trial Duration (days)</label>
                    <input type="number" className="form-control" value={form.trialDays}
                      onChange={e => setField("trialDays", Number(e.target.value))} min="1" />
                    <small className="text-muted">User gets full access for this many days free.</small>
                  </div>
                )}
              </div>

              <hr />

              {/* ── Usage Limits ─────────────────────────────────────────── */}
              <h6 className="text-primary mb-3">Usage Limits</h6>
              <div className="row">
                <div className="form-group col-md-4">
                  <label className="text-black font-w500">Messages/Month (0 = unlimited)</label>
                  <input type="number" className="form-control" value={form.limits.messagesPerMonth}
                    onChange={e => setLimit("messagesPerMonth", e.target.value)} />
                </div>
                <div className="form-group col-md-4">
                  <label className="text-black font-w500">Contacts/Campaign (0 = unlimited)</label>
                  <input type="number" className="form-control" value={form.limits.contactsPerCampaign}
                    onChange={e => setLimit("contactsPerCampaign", e.target.value)} />
                </div>
                <div className="form-group col-md-4">
                  <label className="text-black font-w500">Max WhatsApp Instances</label>
                  <input type="number" className="form-control" value={form.limits.maxInstances}
                    onChange={e => setLimit("maxInstances", e.target.value)} />
                </div>
              </div>

              <hr />

              {/* ── Feature Flags ─────────────────────────────────────────── */}
              <h6 className="text-primary mb-3">Feature Flags</h6>
              <div className="row">
                <FeatureRow label="API Access"       fkey="apiAccess" />
                <FeatureRow label="Webhook Access"   fkey="webhookAccess" />
                <FeatureRow label="Templates"        fkey="templates" />
                <FeatureRow label="Quick Reply Buttons" fkey="buttons" />
                <FeatureRow label="CTA Buttons"      fkey="ctaButtons" />
                <FeatureRow label="List / Menu"      fkey="listMenu" />
                <FeatureRow label="Polls"            fkey="polls" />
                <FeatureRow label="Auto Reply"       fkey="autoReply" />
                <FeatureRow label="Welcome Message"  fkey="welcomeMessage" />
              </div>

              <hr />

              {/* ── Channel Scheduler Add-on ──────────────────────────────── */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-primary mb-0">Channel Scheduler Add-on</h6>
                <button type="button" className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowCS(v => !v)}>
                  {showCS ? "▲ Hide" : "▼ Configure"}
                </button>
              </div>

              {showCS && (
                <div className="border rounded p-3 mb-3" style={{ background: "#f8f9fa" }}>
                  <div className="row">
                    <div className="form-group col-md-4">
                      <label className="text-black font-w500">Enable Channel Scheduler</label>
                      <select className="form-control"
                        value={form.channelScheduler.enabled ? "Yes" : "No"}
                        onChange={e => setCS("enabled", e.target.value === "Yes")}>
                        <option value="No">No (Hidden from users)</option>
                        <option value="Yes">Yes (Active)</option>
                      </select>
                    </div>
                    <div className="form-group col-md-4">
                      <label className="text-black font-w500">Max Channels</label>
                      <input type="number" className="form-control" min="0"
                        value={form.channelScheduler.maxChannels}
                        onChange={e => setCSNum("maxChannels", e.target.value)} />
                    </div>
                    <div className="form-group col-md-4">
                      <label className="text-black font-w500">Schedule Days Ahead</label>
                      <input type="number" className="form-control" min="1"
                        value={form.channelScheduler.maxScheduleDaysAhead}
                        onChange={e => setCSNum("maxScheduleDaysAhead", e.target.value)} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="form-group col-md-6">
                      <label className="text-black font-w500">Max Posts / Channel / Day (0 = unlimited)</label>
                      <input type="number" className="form-control" min="0"
                        value={form.channelScheduler.maxPostsPerChannelPerDay}
                        onChange={e => setCSNum("maxPostsPerChannelPerDay", e.target.value)} />
                    </div>
                    <div className="form-group col-md-6">
                      <label className="text-black font-w500">Max Posts / Channel / Month (0 = unlimited)</label>
                      <input type="number" className="form-control" min="0"
                        value={form.channelScheduler.maxPostsPerChannelPerMonth}
                        onChange={e => setCSNum("maxPostsPerChannelPerMonth", e.target.value)} />
                    </div>
                  </div>
                  <div className="row">
                    {[
                      { key: "allowImage",    label: "Image Posts" },
                      { key: "allowVideo",    label: "Video Posts" },
                      { key: "allowDocument", label: "Document Posts" },
                      { key: "allowPoll",     label: "Poll Posts" },
                      { key: "allowBulkExcel", label: "Bulk Excel Upload" },
                      { key: "allowRecurring", label: "Recurring Posts" },
                      { key: "allowTemplates", label: "Post Templates" },
                      { key: "allowAICheck",   label: "AI Health Check" },
                      { key: "allowAutopilot", label: "Autopilot (auto content)" },
                    ].map(({ key, label }) => (
                      <div className="form-group col-md-3" key={key}>
                        <label className="text-black font-w500">{label}</label>
                        <select className="form-control"
                          value={form.channelScheduler[key] ? "Yes" : "No"}
                          onChange={e => setCS(key, e.target.value === "Yes")}>
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                      </div>
                    ))}
                    {form.channelScheduler.allowAutopilot && (
                      <>
                        <div className="form-group col-md-3">
                          <label className="text-black font-w500">Max Flows (0 = unlimited)</label>
                          <input type="number" className="form-control" min="0"
                            value={form.channelScheduler.maxSources}
                            onChange={e => setCSNum("maxSources", e.target.value)} />
                        </div>
                        <div className="form-group col-md-3">
                          <label className="text-black font-w500">AI Posts / Month (0 = unlimited)</label>
                          <input type="number" className="form-control" min="0"
                            value={form.channelScheduler.maxAIPostsPerMonth}
                            onChange={e => setCSNum("maxAIPostsPerMonth", e.target.value)} />
                        </div>
                      </>
                    )}
                    {form.channelScheduler.allowTemplates && (
                      <div className="form-group col-md-3">
                        <label className="text-black font-w500">Max Templates</label>
                        <input type="number" className="form-control" min="0"
                          value={form.channelScheduler.maxTemplates}
                          onChange={e => setCSNum("maxTemplates", e.target.value)} />
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>{/* end modal-body */}

            <div className="modal-footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn-danger">
                Discard
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Saving…" : editId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* ── Plans Table ──────────────────────────────────────────────────── */}
      <Fragment>
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mr-auto">Plans</h4>
              <button className="btn btn-primary" onClick={openCreate}>
                + Create Plan
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center mt-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : plans.length === 0 ? (
                <p>No plans found. Create your first plan.</p>
              ) : (
                <Table responsive className="w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Cycle</th>
                      <th>Regular</th>
                      <th>Sale</th>
                      <th>Days</th>
                      <th>Msgs/Mo</th>
                      <th>Contacts</th>
                      <th>Trial</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((p, i) => (
                      <tr key={p.id}>
                        <td>{i + 1}</td>
                        <td><strong>{p.name}</strong></td>
                        <td>{p.billingCycle}</td>
                        <td>₹{p.regularPrice}</td>
                        <td>₹{p.salePrice}</td>
                        <td>{p.days}d</td>
                        <td>{p.limits?.messagesPerMonth ?? 0}</td>
                        <td>{p.limits?.contactsPerCampaign ?? 0}</td>
                        <td>
                          {p.hasTrial
                            ? <Badge variant="info">{p.trialDays}d Trial</Badge>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <Badge variant={p.isActive ? "success" : "danger"}>
                            {p.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td>
                          <button className="btn btn-xs btn-primary mr-1" onClick={() => openEdit(p)}>Edit</button>
                          <button className="btn btn-xs btn-danger" onClick={() => onDelete(p)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </Fragment>
    </>
  );
};

export default Plans;
